import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { useTheme } from '../../../contexts/ThemeContext';

// --- CONFIGURATION DES 12 SERVICES ---
const slides = [
  {
    id: 0,
    value: 'real-estate',
    category: 'Immobilier',
    introText: "Votre partenaire de confiance pour l'habitat.",
    badge: 'Location & Vente',
    desc: 'Gestion, achat et location de biens immobiliers résidentiels et commerciaux de prestige.',
    image: "/assets/images/sliders/0.png",
    icon: "Home",
    color: "#2563eb"
  },
  {
    id: 1,
    value: 'vehicle-rental',
    category: 'Transport',
    introText: "La mobilité sans compromis pour vos affaires.",
    badge: 'Location de Véhicules',
    desc: 'Location de véhicules de luxe avec chauffeurs pour faciliter vos missions professionnelles.',
    image: "/assets/images/sliders/1.png",
    icon: "Car",
    color: "#f59e0b"
  },
  {
    id: 2,
    value: 'business-consulting',
    category: 'Consulting',
    introText: "Des stratégies audacieuses pour votre croissance.",
    badge: 'Conseil aux Entreprises',
    desc: 'Audits stratégiques et solutions opérationnelles pour accélérer la croissance de votre firme.',
    image: "/assets/images/sliders/2.png",
    icon: "Briefcase",
    color: "#0f766e"
  },
  {
    id: 3,
    value: 'legal-services',
    category: 'Juridique',
    introText: "La sécurité juridique au service de votre réussite.",
    badge: 'Services Juridiques',
    desc: 'Expertise juridique complète pour assurer la conformité et la sécurité de vos activités.',
    image: "/assets/images/sliders/3.png",
    icon: "Scale",
    color: "#475569"
  },
  {
    id: 4,
    value: 'financial-advisory',
    category: 'Finance',
    introText: "Optimisez vos actifs, sécurisez votre avenir.",
    badge: 'Conseil Financier',
    desc: 'Stratégies d\'investissement performantes et optimisation fiscale de votre patrimoine.',
    image: "/assets/images/sliders/4.png",
    icon: "TrendingUp",
    color: "#16a34a"
  },
  {
    id: 5,
    value: 'it-infrastructure',
    category: 'Technologie',
    introText: "La transformation numérique à portée de main.",
    badge: 'Infrastructure IT',
    desc: 'Installation de réseaux, gestion de serveurs et solutions de cybersécurité avancées.',
    image: "/assets/images/sliders/5.png",
    icon: "Server",
    color: "#7c3aed"
  },
  {
    id: 6,
    value: 'office-supplies',
    category: 'Logistique',
    introText: "Tout l'équipement pour un bureau performant.",
    badge: 'Fournitures de Bureau',
    desc: 'Fourniture intégrale d\'équipements de bureau et consommables pour votre entreprise.',
    image: "/assets/images/sliders/6.png",
    icon: "Package",
    color: "#ea580c"
  },
  {
    id: 7,
    value: 'event-management',
    category: 'Événementiel',
    introText: "Créez des moments corporatifs inoubliables.",
    badge: "Gestion d'Événements",
    desc: 'Planification et gestion clé en main de vos séminaires et conférences d\'entreprise.',
    image: "/assets/images/sliders/7.png",
    icon: "Calendar",
    color: "#db2777"
  },
  {
    id: 8,
    value: 'security-services',
    category: 'Sécurité',
    introText: "Une vigilance constante pour votre sérénité.",
    badge: 'Services de Sécurité',
    desc: 'Gardiennage qualifié et surveillance électronique pour la protection de vos sites.',
    image: "/assets/images/sliders/8.png",
    icon: "Shield",
    color: "#b91c1c"
  },
  {
    id: 9,
    value: 'logistics',
    category: 'Fret',
    introText: "L'efficacité logistique du point A au point B.",
    badge: 'Logistique & Fret',
    desc: 'Solutions de transport de marchandises et gestion fluide de votre chaîne logistique.',
    image: "/assets/images/sliders/9.png",
    icon: "Truck",
    color: "#0284c7"
  },
  {
    id: 10,
    value: 'maintenance',
    category: 'Facility',
    introText: "Vos installations maintenues à la perfection.",
    badge: 'Maintenance',
    desc: 'Maintenance technique préventive et nettoyage professionnel de vos installations.',
    image: "/assets/images/sliders/10.png",
    icon: "Wrench",
    color: "#65a30d"
  },
  {
    id: 11,
    value: 'other',
    category: 'Sur Mesure',
    introText: "Des solutions uniques pour des défis uniques.",
    badge: 'Services Sur Mesure',
    desc: 'Développement de solutions uniques et adaptées à vos défis opérationnels spécifiques.',
    image: "/assets/images/sliders/11.png",
    icon: "PlusCircle",
    color: "#6366f1"
  }
];

const HeroSection = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const { scrollY } = useScroll();

  // Sync header color with current slide
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('hero-color-change', { detail: { color: slides[currentSlide].color } }));
  }, [currentSlide]);

  // Cycle automatique du slider
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const y1 = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);
  const scale = useTransform(scrollY, [0, 300], [1, 0.95]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 10;
      const y = (e.clientY / window.innerHeight - 0.5) * 10;
      setMousePosition({ x, y });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (delay) => ({
      opacity: 1,
      y: 0,
      transition: { delay, duration: 0.8, ease: "easeOut" }
    })
  };

  const slideVariants = {
    enter: { opacity: 0, scale: 1.1, filter: "blur(10px)" },
    center: { opacity: 1, scale: 1, filter: "blur(0px)" },
    exit: { opacity: 0, scale: 0.95, filter: "blur(5px)" }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-white dark:bg-slate-900">

      {/* BACKGROUND INTELLIGENT */}
      <motion.div
        className="absolute inset-0 transition-colors duration-[1500ms] ease-in-out opacity-0 dark:opacity-100"
        style={{
          background: `radial-gradient(circle at 70% 50%, ${slides[currentSlide].color}25 0%, #0f172a 65%)`
        }}
      />
      <div className="absolute inset-0 opacity-0 dark:opacity-20 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] brightness-100 contrast-150 mix-blend-overlay"></div>

      {/* Blobs */}
      <motion.div
        animate={{
          x: mousePosition.x * -2,
          y: y1,
          backgroundColor: slides[currentSlide].color
        }}
        className="absolute top-0 right-0 w-[800px] h-[800px] opacity-0 dark:opacity-15 rounded-full blur-[150px] mix-blend-screen transition-colors duration-1000"
      />
      <motion.div
        animate={{
          x: mousePosition.x * 2,
          y: y1,
          backgroundColor: slides[(currentSlide + 1) % slides.length].color
        }}
        className="absolute bottom-0 left-0 w-[700px] h-[700px] opacity-0 dark:opacity-10 rounded-full blur-[120px] mix-blend-screen transition-colors duration-1000"
      />

      {/* CONTENU PRINCIPAL */}
      <motion.div
        className="relative z-10 w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-20 xl:px-32 py-12 lg:py-0"
        style={{ opacity, scale }}>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 xl:gap-32 items-center">

          {/* GAUCHE */}
          <div className="text-slate-900 dark:text-white space-y-8 relative z-20">

            {/* Badge */}
            <motion.div
              initial="hidden"
              animate="visible"
              custom={0}
              variants={textVariants}
              className="inline-flex items-center gap-2 px-4 py-1.5 bg-slate-100 dark:bg-white/5 backdrop-blur-md rounded-full border border-slate-200 dark:border-white/10 shadow-lg hover:bg-slate-200 dark:hover:bg-white/10 transition-colors cursor-default">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: slides[currentSlide].color }}></span>
                <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: slides[currentSlide].color }}></span>
              </span>
              <span className="text-xs md:text-sm font-medium tracking-wide uppercase text-slate-600 dark:text-slate-300">
                Partenaire Multi-Services • RDC
              </span>
            </motion.div>

            {/* Titre & Description */}
            <div className="space-y-4">

              {/* MODIFICATION ICI : Le dégradé est maintenant sur ORAZEK */}
              <motion.h1
                initial="hidden"
                animate="visible"
                custom={0.2}
                variants={textVariants}
                className="text-4xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-tight tracking-tight">
                
                {/* ORAZEK avec Dégradé */}
                <span
                  className="text-transparent bg-clip-text transition-all duration-700 ease-in-out"
                  style={{
                    backgroundImage: `linear-gradient(to right, ${isDark ? 'white' : '#0f172a'}, ${slides[currentSlide].color})`,
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text'
                  }}
                >
                  ORAZEK
                </span>
                
                <br />
                
                {/* Investment en Blanc simple */}
                <span className="text-slate-900 dark:text-white">
                  Investment
                </span>
                
              </motion.h1>

              {/* DESCRIPTION: Texte Changeant + Couleur Catégorie */}
              <motion.div
                initial="hidden"
                animate="visible"
                custom={0.4}
                variants={textVariants}
                className="h-28 md:h-32 relative overflow-hidden max-w-2xl" // Hauteur réservée pour l'animation
              >
                <AnimatePresence mode='wait'>
                  <motion.p
                    key={currentSlide}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -20, opacity: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="text-lg md:text-xl lg:text-2xl text-slate-600 dark:text-slate-300 leading-relaxed absolute top-0 left-0"
                  >
                    {/* Partie 1: Texte personnalisé de la slide */}
                    {slides[currentSlide].introText}
                    <br className="hidden md:block" />

                    {/* Partie 2: "Découvrez notre service de..." */}
                    Découvrez notre service de{' '}
                    <strong
                      style={{ color: slides[currentSlide].color }}
                      className="font-bold transition-colors duration-500"
                    >
                      {slides[currentSlide].category}
                    </strong>.
                  </motion.p>
                </AnimatePresence>
              </motion.div>
            </div>

            {/* Boutons */}
            <motion.div
              initial="hidden"
              animate="visible"
              custom={0.6}
              variants={textVariants}
              className="flex flex-col sm:flex-row gap-4 pt-6">

              <Button
                variant="default"
                size="lg"
                iconName="ArrowRight"
                iconPosition="right"
                onClick={() => navigate('/contact')}
                className="!text-white border-none shadow-lg transition-all hover:scale-105 px-8"
                style={{ backgroundColor: slides[currentSlide].color }}
              >
                Demander un devis
              </Button>

              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate('/services-overview')}
                className="border-slate-300 dark:border-white/20 text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-white/5 hover:border-slate-400 dark:hover:border-white/40 backdrop-blur-sm px-8">
                Tous nos services
              </Button>
            </motion.div>

            {/* Liste services footer */}
            <motion.div
              initial="hidden"
              animate="visible"
              custom={0.8}
              variants={textVariants}
              className="pt-10 border-t border-slate-200 dark:border-white/10 flex flex-wrap gap-x-8 gap-y-3 text-sm md:text-base text-slate-500 dark:text-slate-400 font-medium">
              <span>Immobilier</span>
              <span>•</span>
              <span>Logistique</span>
              <span>•</span>
              <span>IT & Tech</span>
              <span>•</span>
              <span>Finance</span>
              <span>•</span>
              <span className="text-slate-600 dark:text-slate-400">+8 autres secteurs</span>
            </motion.div>
          </div>

          {/* DROITE : SLIDER */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            style={{
              perspective: 1000,
              rotateY: mousePosition.x * 0.5,
              rotateX: -mousePosition.y * 0.5
            }}
            className="relative h-[400px] md:h-[450px] lg:h-[500px] xl:h-[580px] w-full hidden lg:block">

            <div className="relative w-full h-full rounded-2xl p-2 bg-gradient-to-br from-slate-200/50 dark:from-white/10 to-transparent dark:to-white/0 border border-slate-200 dark:border-white/10 backdrop-blur-sm shadow-2xl">
              <div className="relative w-full h-full rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800">

                <AnimatePresence mode='wait'>
                  <motion.div
                    key={currentSlide}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.8, ease: "circOut" }}
                    className="absolute inset-0"
                  >
                    <Image
                      src={slides[currentSlide].image}
                      alt={slides[currentSlide].badge}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/20 to-transparent" />
                  </motion.div>
                </AnimatePresence>

                {/* Carte flottante */}
                <motion.div
                  className="absolute bottom-6 left-6 right-6 bg-white/95 dark:bg-white/95 backdrop-blur-xl p-5 rounded-xl shadow-2xl border border-slate-200 dark:border-white/50 flex items-center gap-4 z-20">

                  <AnimatePresence mode='wait'>
                    <motion.div
                      key={`icon-${currentSlide}`}
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      exit={{ scale: 0, rotate: 180 }}
                      className="w-14 h-14 rounded-full flex items-center justify-center shrink-0 shadow-lg text-white"
                      style={{ backgroundColor: slides[currentSlide].color }}>
                      <Icon name={slides[currentSlide].icon} size={28} />
                    </motion.div>
                  </AnimatePresence>

                  <div className="flex-1 overflow-hidden">
                    <AnimatePresence mode='wait'>
                      <motion.div
                        key={`text-${currentSlide}`}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.4 }}
                      >
                        <h3 className="font-bold text-slate-800 text-lg md:text-xl truncate">
                          {slides[currentSlide].badge}
                        </h3>
                        <p className="text-slate-600 text-sm truncate">
                          {slides[currentSlide].desc}
                        </p>
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </motion.div>

                {/* Dots */}
                <div className="absolute top-4 right-4 flex flex-wrap justify-end gap-1.5 z-20 w-1/2">
                  {slides.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentSlide(idx)}
                      className={`h-1.5 rounded-full transition-all duration-300 shadow-sm ${idx === currentSlide
                        ? 'w-6 bg-white opacity-100'
                        : 'w-1.5 bg-white/40 hover:bg-white/80'
                        }`}
                    />
                  ))}
                </div>

                {/* Barre progression */}
                <motion.div
                  key={currentSlide}
                  className="absolute top-0 left-0 h-1 z-30 shadow-[0_0_10px_currentColor]"
                  style={{ backgroundColor: slides[currentSlide].color, color: slides[currentSlide].color }}
                  initial={{ width: "0%" }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 5, ease: "linear" }}
                />

              </div>
            </div>

            {/* Back decorations */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
              className="absolute -z-10 -top-12 -right-12 w-48 h-48 border border-dashed border-slate-200 dark:border-white/10 rounded-full"
            />
            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
              className="absolute -z-10 bottom-20 -left-12 w-32 h-32 border-2 border-slate-100 dark:border-white/5 rounded-full"
            />

          </motion.div>
        </div>
      </motion.div>

      {/* Scroll Down */}
      <motion.div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 cursor-pointer z-20"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
        onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Découvrir</span>
          <div className="w-5 h-8 border-2 border-slate-300 dark:border-slate-600 rounded-full flex justify-center pt-2">
            <motion.div
              className="w-1 h-1 bg-slate-500 dark:bg-slate-400 rounded-full"
              animate={{ y: [0, 8, 0], opacity: [1, 0, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;